import { useState } from "react";
import { TablesInsert } from "@/integrations/supabase/types";
import { v4 as uuidv4 } from 'uuid';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { QrCodeData } from "@/types/qrCode";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/hooks/ui/use-toast";
import QRCode from "react-qr-code";
import { Trash, Copy, Star, QrCode as QrCodeIcon, Briefcase } from "lucide-react"; // Added Copy icon
import { supabase as supabaseClient } from "@/integrations/supabase/client";

interface QrCodeManagerProps {
  qrCodes: QrCodeData[];
  setQrCodes: (qrCodes: QrCodeData[]) => void;
  selectedBusinessId: string | null;
  onDeleteQrCode: (qrCodeId: string) => void;
  loading: boolean;
}

const QrCodeManager: React.FC<QrCodeManagerProps> = ({ qrCodes, setQrCodes, selectedBusinessId, onDeleteQrCode, loading }) => {
  const [newQrName, setNewQrName] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tagType, setTagType] = useState("qr"); // 'qr' or 'nfc'
  const [usageType, setUsageType] = useState("review"); // 'review', 'loyalty', or 'job-posting'
  const { session } = useAuth();

  const getTagTypeInfo = (type: string | null) => {
    switch (type) {
      case 'review':
        return {
          icon: <Star className="w-4 h-4" />,
          color: 'bg-blue-100 text-blue-800 border-blue-200',
          darkColor: 'dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700',
          label: 'Review Collection'
        };
      case 'loyalty':
        return {
          icon: <QrCodeIcon className="w-4 h-4" />,
          color: 'bg-green-100 text-green-800 border-green-200',
          darkColor: 'dark:bg-green-900 dark:text-green-200 dark:border-green-700',
          label: 'Loyalty Program'
        };
      case 'job-posting':
        return {
          icon: <Briefcase className="w-4 h-4" />,
          color: 'bg-primary/10 text-primary border-primary/20',
          darkColor: '',
          label: 'Job Application'
        };
      default:
        return {
          icon: <QrCodeIcon className="w-4 h-4" />,
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          darkColor: 'dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700',
          label: 'General'
        };
    }
  };

  const handleCreateQrCode = async () => {
    if (!selectedBusinessId || !newQrName.trim()) return;

    // Check for duplicate tag names within the business
    const existingTag = qrCodes.find(
      (qr) => qr.name.toLowerCase() === newQrName.trim().toLowerCase()
    );
    
    if (existingTag) {
      toast.error("Tag name already exists", {
        description: "Please choose a different name for your tag.",
      });
      return;
    }

    // Basic validation for NFC (can be expanded)
    if (tagType === 'nfc') {
        toast.info("NFC tag creation selected. Ensure you have compatible hardware to write the tag.", { duration: 5000 });
        // In a real app, you might trigger WebNFC API here or guide the user
    }

    try {
      const uniquePath = `${Date.now()}-${selectedBusinessId.substring(0, 8)}`;
      const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
      let path: string;
      if (usageType === 'review') {
        path = '/submit-review';
      } else if (usageType === 'loyalty') {
        path = '/loyalty-program';
      } else if (usageType === 'job-posting') {
        path = '/job-details';
      } else {
        path = '/submit-review'; // fallback
      }
      const fullUrl = `${baseUrl}${path}?code=${uniquePath}`;


      if (!session?.access_token) {
        toast.error("No access token found. Please login again.");
        return;
      }

      const newQrCodeData: TablesInsert<'qr_codes'> = {
        id: uuidv4(),
        business_id: selectedBusinessId,
        name: newQrName.trim(),
        url: fullUrl,
        type: usageType,
        unique_url_path: uniquePath, // Add this line
        created_at: new Date().toISOString(), // Use created_at
      };

      const { data: insertedData, error } = await supabaseClient
        .from("qr_codes")
        .insert(newQrCodeData)
        .select();

      if (error) {
        console.error("Error creating tag:", error);
        toast.error("Failed to create tag", {
          description: error instanceof Error ? error.message : String(error) || "Please try again",
        });
        console.log("Supabase error details:", error);
        return;
      }

      const newQr = Array.isArray(insertedData) && insertedData.length > 0 ? insertedData[0] : null;

      if (!newQr) {
         console.error("Tag insertion did not return data.");
         toast.error("Failed to create tag", {
           description: "Could not retrieve created tag data.",
         });
         return;
      }

      setQrCodes([...qrCodes, newQr] as QrCodeData[]);
      toast.success(`${tagType === 'qr' ? 'QR Code' : 'NFC Tag'} created successfully!`);
      setNewQrName("");
      setTagType("qr");
      setUsageType("review");
      setDialogOpen(false);
    } catch (error: unknown) {
      console.error("Error creating tag:", error);
      toast.error("Failed to create tag", {
        description: error instanceof Error ? error.message : String(error) || "Please try again",
      });
      console.log("Error details:", error instanceof Error ? error.message : String(error), error.stack);
    }
  };

  const copyToClipboard = (text: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
        toast("Copied URL to clipboard!");
      } else {
        // Fallback for older browsers/non-secure contexts
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed"; // Prevent scrolling to bottom of page in MS Edge.
        textArea.style.top = "0";
        textArea.style.left = "0";
        textArea.style.width = "2em";
        textArea.style.height = "2em";
        textArea.style.padding = "0";
        textArea.style.border = "none";
        textArea.style.outline = "none";
        textArea.style.boxShadow = "none";
        textArea.style.background = "transparent";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            toast("Copied URL to clipboard!");
          } else {
            toast.error("Failed to copy URL. Please try again.");
          }
        } catch (err) {
          toast.error("Failed to copy URL. Please try again.");
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error("Failed to copy URL to clipboard:", error);
      toast.error("Failed to copy URL. Please try again.");
    }
  };


  return (
    <div>
      {qrCodes.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {qrCodes.map((qrCode) => {
            const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''; // Handle SSR if needed
            let path: string;
            if (qrCode.type === 'review') {
              path = '/submit-review';
            } else if (qrCode.type === 'loyalty') {
              path = '/loyalty-program';
            } else if (qrCode.type === 'job-posting') {
              path = '/job-details';
            } else {
              path = '/submit-review'; // fallback
            }
            const qrCodeUrl = `${baseUrl}${path}?code=${qrCode.unique_url_path}`; // Use unique_url_path here

            return (
              <Card key={qrCode.id} className="overflow-hidden flex flex-col">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">{qrCode.name}</CardTitle>
                    <Badge 
                      variant="outline" 
                      className={`${getTagTypeInfo(qrCode.type).color} ${getTagTypeInfo(qrCode.type).darkColor} flex items-center gap-1`}
                    >
                      {getTagTypeInfo(qrCode.type).icon}
                      {getTagTypeInfo(qrCode.type).label}
                    </Badge>
                  </div>
                  <CardDescription>
                    Created: {new Date(qrCode.created_at).toLocaleDateString('en-GB').replace(/\//g, '.')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center justify-center flex-grow py-4 bg-gray-50 dark:bg-muted/50">
                  {/* Only show QR code for 'qr' type, maybe an NFC icon otherwise? */}
                  {/* Assuming qrCode object might have a 'tag_format' field later */}
                  <div className="w-[150px] h-[150px] flex items-center justify-center mb-2">
                     <QRCode value={qrCodeUrl} size={150} level="M" className="h-auto max-w-full" />
                  </div>
                  <div className="flex items-center w-full px-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400 break-all flex-grow mr-1">
                      {qrCodeUrl}
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="flex-shrink-0"
                      onClick={() => copyToClipboard(qrCodeUrl)}
                    >
                      <Copy className="h-4 w-4" />
                      <span className="sr-only">Copy URL</span>
                    </Button>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-end pt-2">
                  <Button variant="destructive" size="sm" onClick={() => onDeleteQrCode(qrCode.id)} disabled={loading}>
                    <Trash className="h-4 w-4 mr-1" /> Delete
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      ) : selectedBusinessId ? (
        <div className="mt-8 text-center">
          <p>No QR codes or NFC tags found for this business.</p>
        </div>
      ) : null}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogTrigger asChild>
          <Button className="mt-6">Create New Tag</Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New Tag</DialogTitle>
            <DialogDescription>
              Configure and create a new QR code or NFC tag link.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="qr-name">Name</Label>
              <Input
                id="qr-name"
                placeholder="Table 1, Front Door, Loyalty Check-in"
                value={newQrName}
                onChange={(e) => setNewQrName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Descriptive name for identification (e.g., location, purpose).
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="tag-type">Tag Type</Label>
              <Select value={tagType} onValueChange={setTagType}>
                <SelectTrigger id="tag-type">
                  <SelectValue placeholder="Select tag type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="qr">QR Code</SelectItem>
                  <SelectItem value="nfc">NFC Tag</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Select QR for scannable codes, NFC for tap interactions (requires hardware).
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="usage-type">Usage Type</Label>
              <Select value={usageType} onValueChange={setUsageType}>
                <SelectTrigger id="usage-type">
                  <SelectValue placeholder="Select usage type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="review">Review Collection</SelectItem>
                  <SelectItem value="loyalty">Loyalty Program</SelectItem>
                  <SelectItem value="job-posting">Job Application</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Determines the link destination (review form, loyalty page, or job application).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateQrCode} disabled={!newQrName.trim() || !selectedBusinessId}>
              Create Tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QrCodeManager;
